import { useUser, useClerk } from '@clerk/clerk-react';
import { useState, useEffect, useRef } from 'react';
import { FileSliders, LogOut, Flag, Scale, Settings, Gift } from 'lucide-react';
import Preferences from '@/auth/Preferences';
import { useQuery } from '@tanstack/react-query';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';

const AccountsMenu = ({ isMorePage = false }) => {
    const { user } = useUser();
    const { signOut, openUserProfile } = useClerk();
    const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [userRole, setUserRole] = useState('Student');
    const menuRef = useRef(null);
    const token = useToken();

    const { data: rolesData } = useQuery({
        queryKey: ['user', 'roles', user?.id],
        queryFn: () => api.getUserRoles(user?.id, token || ''),
        enabled: !!token && !!user?.id,
        select: d => d.roles ?? d,
    });

    useEffect(() => {
        if (rolesData && Array.isArray(rolesData) && rolesData.length > 0) {
            const roles = rolesData.map(r => r.charAt(0).toUpperCase() + r.slice(1));
            setUserRole(roles.join(', '));
        } else {
            setUserRole('Student');
        }
    }, [rolesData]);

    useEffect(() => {
        const handleClickOutside = event => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    const menuItems = [
        {
            label: 'Manage Account',
            icon: Settings,
            onClick: () => {
                setIsMenuOpen(false);
                openUserProfile();
            },
        },
        {
            label: 'My Preferences',
            icon: FileSliders,
            onClick: () => {
                setIsMenuOpen(false);
                setIsPreferencesOpen(true);
            },
        },
        {
            label: 'Request Feature or Report Bug',
            icon: Flag,
            onClick: () => {
                setIsMenuOpen(false);
                window.open('https://teable.zinduaschool.com/share/shr02vkM9ypwxebB85H/view', '_blank');
            },
        },
        {
            label: 'Refer & Earn (Affiliate Program)',
            icon: Gift,
            onClick: () => {
                setIsMenuOpen(false);
                window.open('https://zinduaschool.com/refer/', '_blank');
            },
        },
        {
            label: 'Legal Policies (Terms/Privacy)',
            icon: Scale,
            onClick: () => {
                setIsMenuOpen(false);
                window.open(
                    'https://zinduaschool.com/legal/?srsltid=AfmBOoqOlEpreLAWY_4aXikZqmMIQ-xz0oJ1N_XTy_ZHRleJJmVWyqAf#terms',
                    '_blank'
                );
            },
        },
        {
            label: 'Sign Out',
            icon: LogOut,
            onClick: async () => {
                setIsMenuOpen(false);
                await signOut();
                window.location.href = '/';
            },
        },
    ];

    return (
        <>
            <div ref={menuRef} className="relative">
                <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="flex gap-2 items-center cursor-pointer rounded-lg hover:bg-neutral-100 transition-colors"
                >
                    <img
                        src={user?.imageUrl}
                        alt={user?.username}
                        className={`w-10 h-10 rounded-full object-cover border border-neutral-300`}
                    />
                    {!isMorePage && <h6 className="md:hidden">{user ? user.username : 'Guest'}</h6>}
                </button>

                {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-75 bg-neutral-50 rounded-lg shadow-lg border border-neutral-300 py-2 z-50">
                        {/* User Profile Section */}
                        <div className="flex items-center gap-3 px-4 py-3">
                            <img
                                src={user?.imageUrl}
                                alt={user?.username}
                                className={`w-12 h-12 rounded-full object-cover border border-neutral-300`}
                            />
                            <div className="flex flex-col min-w-0">
                                <span className="font-medium text-neutral-900 truncate capitalize">
                                    {user?.fullName || user?.firstName || 'User'}
                                </span>
                                <span className="text-sm text-neutral-600 truncate">@{user?.username || 'username'}</span>
                                <span className="text-xs text-primary font-medium">{userRole}</span>
                            </div>
                        </div>
                        <div className="border-t border-neutral-300 my-1" />
                        {menuItems.map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <div key={index}>
                                    <button
                                        onClick={item.onClick}
                                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-neutral-700 hover:bg-neutral-100 transition-colors text-sm"
                                    >
                                        <Icon className="w-4 h-4 shrink-0" />
                                        <span>{item.label}</span>
                                    </button>
                                    {(index + 1) % 2 === 0 && index !== menuItems.length - 1 && (
                                        <div className="border-t border-neutral-300 my-1" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Preferences isOpen={isPreferencesOpen} onClose={() => setIsPreferencesOpen(false)} />
        </>
    );
};

export default AccountsMenu;
